import React, { useContext, useEffect, useState } from 'react';
import courseService from 'ApiService/Admin/courseService';
import Nodatafound from 'pages/Common/Nodatafound';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import InputField from 'components/fields/InputField';
import { SlArrowLeft } from 'react-icons/sl';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const Index = () => {
  const { state } = useContext(AuthContext);

  const role = state?.user?.role;
  const selectedBranch = state?.selectedBranch;

  const [data, setData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [isGlobal, setIsGlobal] = useState(false);
  const [branchId, setBranchId] = useState(null);

  const [updateConfirmation, setUpdateConfirmation] = useState({
    isOpen: false,
    courseData: null,
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    courseToDelete: null,
  });

  /* ================= FETCH COURSES ================= */
  const fetchCourses = async () => {
    if (role === 'superadmin' && !selectedBranch) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await courseService.getAllCourses(selectedBranch);
      if (res.data.success) setData(res.data.courses || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error fetching courses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [role, selectedBranch]);

  /* ================= FETCH DEPARTMENTS ================= */
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await courseService.getAllDepartments();
        if (res.data.success) setDepartments(res.data.data || []);
      } catch {
        toast.error('Failed to load departments');
      }
    };
    fetchDepartments();
  }, []);

  const selectedCourseDetails = data.find(
    (course) => course._id === selectedCourseId
  );

  /* ================= EDIT ================= */
  const handleEdit = (course) => {
    setSelectedCourse({ ...course });
    setIsEditModalOpen(true);

    if (course.branch) {
      setIsGlobal(false);
      setBranchId(course.branch._id);
    } else {
      setIsGlobal(true);
      setBranchId(null);
    }
  };

  const handleUpdateConfirm = (e) => {
    e.preventDefault();

    setUpdateConfirmation({
      isOpen: true,
      courseData: {
        courseName: selectedCourse.courseName,
        department: selectedCourse.department?._id,
        duration: Number(selectedCourse.duration),
        prerequisites: selectedCourse.prerequisites || '',
        branch: isGlobal ? null : branchId,
      },
    });
  };

  const performUpdate = async () => {
    try {
      const res = await courseService.updateCourse(
        selectedCourse._id,
        updateConfirmation.courseData
      );
      if (res.data.success) {
        toast.success('Course updated successfully');
        setIsEditModalOpen(false);
        fetchCourses();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    } finally {
      setUpdateConfirmation({ isOpen: false, courseData: null });
    }
  };

  /* ================= DELETE ================= */
  const performDelete = async () => {
    try {
      const res = await courseService.deleteCourse(
        deleteConfirmation.courseToDelete._id
      );
      if (res.data.success) {
        toast.success('Course deleted successfully');
        fetchCourses();
        setSelectedCourseId(null);
      }
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleteConfirmation({ isOpen: false, courseToDelete: null });
    }
  };

  return (
    <div className="mt-3 flex h-full w-full flex-col">
      <div className="h-full w-full rounded-[20px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <BallTriangle color="#5431ed" />
          </div>
        ) : data.length === 0 ? (
          <Nodatafound title="No courses found" />
        ) : selectedCourseId ? (
          /* ================= DETAIL VIEW ================= */
          <div className="rounded-[20px] bg-white p-6 shadow-lg">
            <button
              onClick={() => setSelectedCourseId(null)}
              className="mb-6 flex items-center text-brand-500"
            >
              <SlArrowLeft className="mr-2" />
              Back to Courses
            </button>

            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {selectedCourseDetails.courseName}
              </h2>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(selectedCourseDetails)}
                  className="rounded bg-blue-500 px-4 py-2 text-white"
                >
                  Edit
                </button>
                <button
                  onClick={() =>
                    setDeleteConfirmation({
                      isOpen: true,
                      courseToDelete: selectedCourseDetails,
                    })
                  }
                  className="rounded bg-red-500 px-4 py-2 text-white"
                >
                  Delete
                </button>
              </div>
            </div>

            <span
              className={`mb-4 inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                selectedCourseDetails.branch
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {selectedCourseDetails.branch?.name || 'Global Course'}
            </span>

            <div className="grid grid-cols-2 gap-6">
              <Info
                label="Department"
                value={selectedCourseDetails.department?.name}
              />
              <Info
                label="Duration"
                value={`${selectedCourseDetails.duration} months`}
              />
              <Info
                label="Prerequisites"
                value={selectedCourseDetails.prerequisites || 'N/A'}
              />
            </div>
          </div>
        ) : (
          /* ================= CARD VIEW ================= */
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.map((course) => (
              <div
                key={course._id}
                onClick={() => setSelectedCourseId(course._id)}
                className="relative cursor-pointer rounded-[20px] border bg-white p-6 shadow"
              >
                <span
                  className={`absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${
                    course.branch
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {course.branch?.name || 'Global'}
                </span>

                <h3 className="mb-2 text-xl font-bold">{course.courseName}</h3>

                <div className="space-y-1 text-sm text-gray-600">
                  <p>
                    <b>Department:</b> {course.department?.name}
                  </p>
                  <p>
                    <b>Duration:</b> {course.duration} months
                  </p>
                  <p>
                    <b>Batches:</b> {course.batches?.length || 0}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ================= EDIT MODAL ================= */}
        {isEditModalOpen && selectedCourse && (
          <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
            <div className="w-full max-w-md rounded-xl bg-white p-6">
              <h2 className="mb-4 text-xl font-bold">Edit Course</h2>

              <form onSubmit={handleUpdateConfirm} className="space-y-4">
                <InputField
                  label="Course Name"
                  value={selectedCourse.courseName}
                  onChange={(e) =>
                    setSelectedCourse({
                      ...selectedCourse,
                      courseName: e.target.value,
                    })
                  }
                />

                <select
                  value={selectedCourse.department?._id || ''}
                  onChange={(e) =>
                    setSelectedCourse({
                      ...selectedCourse,
                      department: { _id: e.target.value },
                    })
                  }
                  className="w-full rounded border px-3 py-2"
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>

                <InputField
                  label="Duration (months)"
                  type="number"
                  value={selectedCourse.duration}
                  onChange={(e) =>
                    setSelectedCourse({
                      ...selectedCourse,
                      duration: e.target.value,
                    })
                  }
                />

                <InputField
                  label="Prerequisites"
                  value={selectedCourse.prerequisites || ''}
                  onChange={(e) =>
                    setSelectedCourse({
                      ...selectedCourse,
                      prerequisites: e.target.value,
                    })
                  }
                />

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isGlobal}
                    onChange={(e) => {
                      setIsGlobal(e.target.checked);
                      if (e.target.checked) setBranchId(null);
                    }}
                  />
                  Make Global Course
                </label>

                {!isGlobal && (
                  <select
                    value={branchId || ''}
                    onChange={(e) => setBranchId(e.target.value)}
                    className="w-full rounded border px-3 py-2"
                    required
                  >
                    <option value="">Select Branch</option>
                    <option value={selectedBranch}>Current Branch</option>
                  </select>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="rounded border px-4 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded bg-brand-500 px-4 py-2 text-white"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={updateConfirmation.isOpen}
          onClose={() =>
            setUpdateConfirmation({ isOpen: false, courseData: null })
          }
          onConfirm={performUpdate}
          title="Confirm Update"
          message="Are you sure?"
        />

        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={() =>
            setDeleteConfirmation({ isOpen: false, courseToDelete: null })
          }
          onConfirm={performDelete}
          title="Confirm Delete"
          message="Delete this course?"
        />
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="rounded bg-gray-50 p-4">
    <h4 className="text-sm font-semibold text-gray-600">{label}</h4>
    <p className="text-gray-800">{value || 'N/A'}</p>
  </div>
);

export default Index;
